import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import 'moment/locale/he'; // Hebrew
import 'moment/locale/en-gb'; // English (Great Britain)
import 'moment/locale/ar'; // Arabic
import { toast } from "react-toastify";
import AuthService from '../../../Services/auth.service';
import UserService from '../../../Services/user.service';
import { LanguageSelector } from '../../Login/SubComponents/LanguageSelector';
import { FontSizeSelector } from '../../Login/SubComponents/FontSizeSelector';
import selectorStyles from '../../Login/SubComponents/LanguageSelector.module.css';
import Styles from './UpperPanelDetails.module.css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedIcon from '@mui/icons-material/Verified';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StarRateIcon from '@mui/icons-material/StarRate';
import CheckInOutModal from '../CheckInOutModal/CheckInOutModal';
import KeyboardShortcuts from '../../KeyboardShortcuts/KeyboardShortcuts.jsx';
import ReportingFollowUpModal from '../../ReportingFollowUp/ReportingFollowUpModal';
import { PermissionsContext } from '../../../Contexts/Permissions.js';
import Constants from '../../../assests/Constants/constants.js';
import isoCertificate from '../../../assests/certificate/iso.PDF';
import taxAuthorityCertificate from '../../../assests/certificate/תעודת רישום.pdf';

import { useNotificationCenter } from '../../../Contexts/NotificationCenter';
import { IoNotifications, IoNotificationsOutline } from 'react-icons/io5';
import { openUserFeedbackModal } from '../UserFeedbackModal/UserFeedbackPromptHost';
import WhatsNewButton from '../WhatsNew/WhatsNewButton';

const SUPPORT_PHONE = '046590004';
const SUPPORT_WHATSAPP_DISPLAY = '053-9470430';
const SUPPORT_WHATSAPP_INTL = '972539470430';
const DOCUMENTS_WHATSAPP_DISPLAY = '054-240-4564';
const DOCUMENTS_WHATSAPP_INTL = '972542404564';
const DOCUMENTS_EMAIL = 'documents@xtram.net';

/** First letter of the first two words, for the avatar disc. */
const getInitials = name =>
    (name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase();

const isPresignedUrlExpired = url => {
    try {

        // Check if the URL is null, undefined, or empty
        if (!url || url.trim() === "") {
            console.warn("The URL is null, empty, or undefined.");
            return false;
        }

        const urlParams = new URLSearchParams(url.split('?')[1]);
        const expiresInSeconds = parseInt(urlParams.get('X-Amz-Expires'), 10);
        const amzDate = urlParams.get('X-Amz-Date');

        if (!expiresInSeconds || !amzDate) {
            console.warn("Missing necessary parameters in the URL.");
            return true;
        }

        // Convert X-Amz-Date (which is in UTC time) to a JavaScript Date object
        const expirationTime = new Date(amzDate.substring(0, 8) + 'T' + amzDate.substring(9, 15) + 'Z');

        // Add the expiration time in seconds to the X-Amz-Date to get the exact expiration time
        expirationTime.setSeconds(expirationTime.getSeconds() + expiresInSeconds);

        // Get the current time
        const currentTime = new Date();

        // Return true if the URL has expired
        return currentTime > expirationTime;
    } catch (error) {
        console.error("Error checking URL expiration:", error);
        return false; // If there's an error, treat the URL as expired to force a reload
    }
};

function UpperPanelDetails({ client = null }) {
    const { i18n, t } = useTranslation();
    const [direction, setDirection] = useState('rtl'); // Default to 'rtl'
    const navigate = useNavigate();
    const location = useLocation();
    const isMainPage = location.pathname.toLowerCase() === '/main';
    const [user, setUser] = useState();
    const [isAccountant, setIsAccountant] = useState(false);
    const [loading, setLoading] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const handlePressOpen = () => setCiOpen(true);
    const handlePressClose = () => setCiOpen(false);
    const [isOpen, setIsOpen] = useState(false);
    const [ciOpen, setCiOpen] = useState(false);
    const [token, setToken] = useState('');
    const [shouldPulse, setShouldPulse] = useState(false);
    const [helpAnchorEl, setHelpAnchorEl] = useState(null);
    const [helpTooltipOpen, setHelpTooltipOpen] = useState(false);
    const isHelpOpen = Boolean(helpAnchorEl);
    const handleHelpOpen = (event) => {
        setHelpTooltipOpen(false);
        setHelpAnchorEl(event.currentTarget);
    };
    const handleHelpClose = () => setHelpAnchorEl(null);

    const { permissionsState, userTypeContext, clientPermissionsState, resetPermissionsState } = useContext(PermissionsContext);
    // ManageOnly clients are restricted to CRM + Archive only; the keyboard-shortcut overlay
    // exposes navigation/actions that don't apply, so suppress it for that case.
    const isManageOnly = !!clientPermissionsState?.ManageOnly;
    const canShowKeyboardShortcuts =
        !isManageOnly && client && (userTypeContext?.toString() !== Constants.UserType.Client || permissionsState.Premium);
    // Same audience as the CRM menu's Monthly Reporting Tracking item.
    const canOpenReportingFollowUp = !!client
        && !permissionsState?.ViewSubAccountantClients
        && userTypeContext?.toString() !== Constants.UserType.Client;
    const [reportingFollowUpOpen, setReportingFollowUpOpen] = useState(false);

    const { openNotifications, unreadCount } = useNotificationCenter();
    // Set the locale in Moment.js based on the current language
    moment.locale(i18n.language);

    useEffect(() => {
        // Check for saved language in localStorage
        const currentLanguage = localStorage.getItem('language') || 'he'; // Default to 'he' if no language is stored
        i18n.changeLanguage(currentLanguage);
    }, [i18n]);

    useEffect(() => {
        // Update direction based on the current language
        if (i18n.language === 'he' || i18n.language === 'ar') {
            setDirection('rtl');
        } else {
            setDirection('ltr');
        }
    }, [i18n.language]);

    const handleLogout = async () => {
        try {
            resetPermissionsState?.();
            await AuthService.logout();
            navigate('/login', { replace: true });
        } catch (error) {
            navigate('/login', { replace: true });
        }
    };

    const checkLogoutTime = async () => {
        try {
            const Token = await UserService.getCurrentToken();
            const validTokenDate = await AuthService.CheckTokenValidation(Token);
            if (!validTokenDate.isValid) {
                toast.info(t('YouNeedToLogInAgain'));
                handleLogout();
            }
        } catch (error) {
            toast.info(t('YouNeedToLogInAgain'));
            handleLogout();
        }
    };

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const Token = await UserService.getCurrentToken();
            // const validTokenDate = await AuthService.CheckTokenValidation(Token);
            setToken(Token);
            // if (!validTokenDate.isValid) {
            //     handleLogout();
            // }
            let User = await UserService.getCurrentUser();
            const userType = await UserService.getCurrentUserType();
            if (User && Token && userType) {

                if (isPresignedUrlExpired(User.profilePicture)) {
                    User = await UserService.UpdateUser(User.accountantId, Token);
                }
                setUser(User);

                if (userType.toString() === '2') {
                    setIsAccountant(true);
                }


            }

        } catch (error) {
            handleLogout();
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchUserData();
        // checkLogoutTime();
    }, []);

    const getGreeting = () => {
        const currentHour = moment().tz('Asia/Jerusalem').hour();

        if (currentHour < 12) {
            return t('greetingMorning');
        } else if (currentHour < 18) {
            return t('greetingAfternoon');
        } else {
            return t('greetingEvening');
        }
    };

    useEffect(() => {
        if (user && user.profilePicture) {
            const img = new Image();
            img.src = user.profilePicture;
            img.onload = () => {
                setImageLoaded(true);
            };
            img.onerror = () => {
                setImageLoaded(false);
            };

        } else {
            setImageLoaded(false);
        }
    }, [user]);

    const refreshPulseState = () => {
        const stored = localStorage.getItem("checkedIn");
        if (!stored) {
            setShouldPulse(true);          // no record at all → pulse
            return;
        }

        // compare calendar day using local device time
        const sameDay = moment(stored).isSame(moment(), "day");

        setShouldPulse(!sameDay);        // pulse only if NOT today
    };

    /* run once when the component mounts */
    useEffect(() => {
        refreshPulseState();
    }, []);

    /* run again whenever the Check-In/Out modal closes */
    useEffect(() => {
        if (!ciOpen) refreshPulseState();
    }, [ciOpen]);

    useEffect(() => {
        const showTimer = setTimeout(() => setHelpTooltipOpen(true), 1000);
        const hideTimer = setTimeout(() => setHelpTooltipOpen(false), 5500);
        return () => {
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, []);

    const currentDate = moment().tz('Asia/Jerusalem').format('LL'); // Localized date without time

    return (
        <div
            className={`${Styles.UpperPanelDetailsContainer}${isMainPage ? ` ${Styles.UpperPanelDetailsContainerMain}` : ''}`}
            dir={direction}
        >
            {client && (
                <div className={Styles.businessIdentity}>
                    <span className={Styles.businessAvatar} aria-hidden="true">
                        {getInitials(client.name) || <AccountCircleRoundedIcon sx={{ fontSize: 28, color: '#1945a8' }} />}
                    </span>
                    <div className={Styles.businessText}>
                        <span className={Styles.businessLabel}>{t('BusinessName')}:</span>
                        <span className={Styles.businessName}>
                            {client.name}
                            {client.caseId != null && client.caseId !== '' && (
                                <>
                                    <span className={Styles.businessNameSep}> · </span>
                                    <span className={Styles.businessCaseId}>{client.caseId}</span>

                                </>
                            )}
                        </span>
                        <span className={Styles.businessMeta}>
                            {getGreeting()} {user && user.name} · {currentDate}
                        </span>
                    </div>
                </div>
            )}

            {!client &&
                <div className={Styles.UpperPanelDetailsGreetingContainer}>
                    <div>{getGreeting()} {user && user.name}</div>
                    <div>{currentDate}</div>
                </div>
            }


            <div className={Styles.UpperPanelDetailsOperationsContainer}>
                <div className={Styles.actionsRow}>
                    {canShowKeyboardShortcuts &&
                        <KeyboardShortcuts
                            client={client}
                            direction={direction}
                            enableKeyboardListener={false}
                        />
                    }

                    <button
                        type="button"
                        className={`${Styles.actionBtn} ${Styles.actionBtnPulse}`}
                        onClick={handleHelpOpen}
                        aria-label={t('ClickForHelp')}
                    >
                        <Tooltip
                            title={t('ClickForHelp')}
                            open={helpTooltipOpen}
                            arrow
                            placement="bottom"
                            disableFocusListener
                            disableHoverListener={helpTooltipOpen}
                            disableTouchListener={helpTooltipOpen}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: { offset: [0, 6] },
                                        },
                                    ],
                                },
                                tooltip: {
                                    sx: {
                                        fontFamily: 'Heebo, sans-serif',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        bgcolor: 'var(--xtram-blue-app, #304fff)',
                                        px: 1.5,
                                        py: 0.75,
                                    },
                                },
                                arrow: { sx: { color: 'var(--xtram-blue-app, #304fff)' } },
                            }}
                        >
                            <span className={Styles.helpIconInner}>
                                <HelpRoundedIcon
                                    className={Styles.actionIcon}
                                    sx={{ fontSize: 22 }}
                                />
                            </span>
                        </Tooltip>
                    </button>

                    {userTypeContext?.toString() !== Constants.UserType.Client && (
                        <button
                            type="button"
                            className={`${Styles.actionBtn} ${shouldPulse ? Styles.actionBtnPulse : ''}`}
                            onClick={handlePressOpen}
                            aria-label={t('Time')}
                        >
                            <Tooltip title={t('Time')}>
                                <AccessAlarmIcon
                                    className={Styles.actionIcon}
                                    sx={{ fontSize: 22 }}
                                />
                            </Tooltip>
                        </button>
                    )}

                    <button
                        type="button"
                        className={`${Styles.actionBtn} ${Styles.notifBtn}`}
                        onClick={openNotifications}
                        title={t('Notifications')}
                        aria-label={t('Notifications')}
                    >
                        {unreadCount > 0
                            ? <IoNotifications size={22} color="#0e2b58" />
                            : <IoNotificationsOutline size={22} color="#0e2b58" />}
                        {unreadCount > 0 && (
                            <span className={Styles.badgeDot} aria-hidden="true" />
                        )}
                    </button>

                    <div
                        className={selectorStyles.selectorsRow}
                        style={{ position: 'relative', zIndex: 1 }}
                    >
                        <FontSizeSelector />
                        <LanguageSelector />
                    </div>

                    <WhatsNewButton direction={direction} />
                </div>

                <Menu
                    anchorEl={helpAnchorEl}
                    open={isHelpOpen}
                    onClose={handleHelpClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'rtl' ? 'left' : 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: direction === 'rtl' ? 'left' : 'right' }}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            minWidth: 300,
                            maxWidth: 360,
                            borderRadius: 2,
                            boxShadow: '0 8px 24px rgba(49, 79, 255, 0.15)',
                        },
                    }}
                >
                    <div style={{
                        padding: '8px 16px',
                        fontFamily: 'Heebo, sans-serif',
                        fontWeight: 600,
                        fontSize: 14,
                        color: '#314fff',
                        direction,
                        textAlign: direction === 'rtl' ? 'right' : 'left',
                    }}>
                        {t('ContactUs')}
                    </div>
                    <Divider />
                    <MenuItem
                        component="a"
                        href={`tel:${SUPPORT_PHONE}`}
                        onClick={handleHelpClose}
                        sx={{ direction, textAlign: direction === 'rtl' ? 'right' : 'left' }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <PhoneIcon sx={{ color: '#314fff' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('CustomerService')}
                            secondary={SUPPORT_PHONE}
                            primaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 14, fontWeight: 600 }}
                            secondaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 13 }}
                        />
                    </MenuItem>
                    <MenuItem
                        component="a"
                        href={`https://wa.me/${SUPPORT_WHATSAPP_INTL}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleHelpClose}
                        sx={{ direction, textAlign: direction === 'rtl' ? 'right' : 'left' }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <WhatsAppIcon sx={{ color: '#25D366' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('WhatsApp')}
                            secondary={SUPPORT_WHATSAPP_DISPLAY}
                            primaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 14, fontWeight: 600 }}
                            secondaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 13 }}
                        />
                    </MenuItem>
                    <Divider />
                    <div style={{
                        padding: '8px 16px 4px',
                        fontFamily: 'Heebo, sans-serif',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#314fff',
                        direction,
                        textAlign: direction === 'rtl' ? 'right' : 'left',
                    }}>
                        {t('SendDocumentsChannelsTitle')}
                    </div>
                    <MenuItem
                        component="a"
                        href={`https://wa.me/${DOCUMENTS_WHATSAPP_INTL}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleHelpClose}
                        sx={{
                            direction,
                            textAlign: direction === 'rtl' ? 'right' : 'left',
                            alignItems: 'flex-start',
                            whiteSpace: 'normal',
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                            <WhatsAppIcon sx={{ color: '#25D366' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('SendExpensesViaWhatsApp')}
                            secondary={`${DOCUMENTS_WHATSAPP_DISPLAY} — ${t('SendExpensesViaWhatsAppHint')}`}
                            primaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 14, fontWeight: 600 }}
                            secondaryTypographyProps={{
                                fontFamily: 'Heebo, sans-serif',
                                fontSize: 12,
                                whiteSpace: 'normal',
                            }}
                        />
                    </MenuItem>
                    <MenuItem
                        component="a"
                        href={`mailto:${DOCUMENTS_EMAIL}`}
                        onClick={handleHelpClose}
                        sx={{
                            direction,
                            textAlign: direction === 'rtl' ? 'right' : 'left',
                            alignItems: 'flex-start',
                            whiteSpace: 'normal',
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                            <EmailIcon sx={{ color: '#314fff' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('SendDocumentsViaEmail')}
                            secondary={`${DOCUMENTS_EMAIL} — ${t('SendDocumentsViaEmailHint')}`}
                            primaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 14, fontWeight: 600 }}
                            secondaryTypographyProps={{
                                fontFamily: 'Heebo, sans-serif',
                                fontSize: 12,
                                whiteSpace: 'normal',
                            }}
                        />
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        onClick={() => {
                            handleHelpClose();
                            openUserFeedbackModal();
                        }}
                        sx={{ direction, textAlign: direction === 'rtl' ? 'right' : 'left' }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <StarRateIcon sx={{ color: '#f5a623' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('FeedbackMenuItem')}
                            secondary={t('FeedbackMenuItemHint')}
                            primaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 14, fontWeight: 600 }}
                            secondaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 12 }}
                        />
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        component="a"
                        href={taxAuthorityCertificate}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleHelpClose}
                        sx={{ direction, textAlign: direction === 'rtl' ? 'right' : 'left' }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <VerifiedIcon sx={{ color: '#314fff' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('TaxAuthorityCertificate')}
                            primaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 14, fontWeight: 600 }}
                        />
                    </MenuItem>
                    <MenuItem
                        component="a"
                        href={isoCertificate}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleHelpClose}
                        sx={{ direction, textAlign: direction === 'rtl' ? 'right' : 'left' }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <WorkspacePremiumIcon sx={{ color: '#314fff' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('IsoCertificate')}
                            primaryTypographyProps={{ fontFamily: 'Heebo, sans-serif', fontSize: 14, fontWeight: 600 }}
                        />
                    </MenuItem>
                </Menu>
            </div>

            {/* F-key shortcuts: own full-width row so they never widen the page.
                position:relative + low zIndex creates a stacking context so the inner
                shortcut buttons (zIndex:1000) stay below overlays/modals. */}
            {client && canShowKeyboardShortcuts && (
                <div
                    dir={direction}
                    style={{
                        flexBasis: '100%',
                        width: '100%',
                        minWidth: 0,
                        display: 'flex',
                        justifyContent: 'flex-start',
                        position: 'relative',
                        zIndex: 0,
                    }}
                >
                    <KeyboardShortcuts
                        client={client}
                        direction={direction}
                        displayMode="inlineLabels"
                        enableKeyboardListener
                        inlineExcludeKeys={['F10', 'F11', 'F12']}
                        advanceCalculatorInline
                        onOpenReportingFollowUp={canOpenReportingFollowUp ? () => setReportingFollowUpOpen(true) : null}
                    />
                </div>
            )}

            {canOpenReportingFollowUp && reportingFollowUpOpen && (
                <ReportingFollowUpModal
                    open
                    onClose={() => setReportingFollowUpOpen(false)}
                    client={client}
                    user={user}
                    token={token}
                    defaultDate={localStorage.getItem('Date') ? new Date(localStorage.getItem('Date')) : new Date()}
                />
            )}

            {/* Lightbox Popup */}
            {isLightboxOpen && user && user.profilePicture && (
                <Lightbox
                    open={isLightboxOpen}
                    close={() => setIsLightboxOpen(false)}
                    slides={[{ src: user.profilePicture, title: user.name }]}
                />
            )}

            <CheckInOutModal
                open={ciOpen}
                onClose={handlePressClose}
                accountantId={client?.accountantId}
                authToken={token}
                t={t}
            />
        </div>
    );
}

export default UpperPanelDetails;
